/**
 * One picker behind every escape hatch.
 *
 * It arrives with the current pick already selected and tagged, so the CTA stays
 * lit and a thumb-slip onto the hatch costs one tap to undo rather than two.
 *
 * Plan members see their plan first and everything else by recency; everyone
 * else sees the three recency bands, which is why small libraries need no search
 * at all. From twelve workouts up a search field tops the list — the one place
 * in this flow a keyboard is allowed, taken deliberately.
 */

import {
  noMatchesLine,
  pickerGroups,
  pickerSearchPlaceholder,
  showsPickerSearch,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import { WorkoutPickRow } from './WorkoutPickRow';

import type { WorkoutPickerStepProps } from './StartWorkoutSheet.types';

import { CopyRuns } from '@/components/ui/CopyRuns';
import { FilterSearch } from '@/components/ui/Filter';
import { SectionLabel, Text } from '@/components/ui/primitives';

export function WorkoutPickerStep({
  options,
  selectedId,
  onSelect,
  tags,
  query,
  onQueryChange,
  note,
}: WorkoutPickerStepProps): React.JSX.Element {
  const groups = pickerGroups(options, query);
  const showSearch = showsPickerSearch(options);

  return (
    <div data-testid="start-workout-picker">
      {note && (
        <Text as="p" variant="body-sm" color="secondary" className="mb-3.5">
          <CopyRuns
            segments={note}
            strongClassName="font-semibold text-neutral-200"
          />
        </Text>
      )}

      {showSearch && (
        <div className="mb-3">
          <FilterSearch
            value={query}
            onChange={onQueryChange}
            placeholder={pickerSearchPlaceholder(options)}
          />
        </div>
      )}

      {groups.length === 0 && (
        <Text
          as="p"
          variant="body-md"
          color="secondary"
          className="py-6 text-center"
        >
          {noMatchesLine(query)}
        </Text>
      )}

      {groups.map((group, index) => (
        <fieldset key={group.key} className={index === 0 ? '' : 'mt-3.5'}>
          <legend className="mb-1.5 ml-1">
            <SectionLabel>{group.label}</SectionLabel>
          </legend>

          <div className="space-y-2">
            {group.workouts.map((workout) => (
              <WorkoutPickRow
                key={workout.id}
                workout={workout}
                selected={workout.id === selectedId}
                onSelect={onSelect}
                name="start-workout-pick"
                tag={tags[String(workout.id)]?.label}
                tagTone={tags[String(workout.id)]?.tone}
              />
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

export default WorkoutPickerStep;
